/**
 * SmartPollingManager - Intelligent API refresh strategies
 *
 * Dual-source data fetching:
 * - Sidewinder (2140data.net): Historical timechain data, Lightning, supply
 * - Diamondback (2140data.com): Real-time network stats, difficulty, mempool
 *
 * Replaces the 5-minute polling with event-driven and strategic intervals:
 * - On every block: Diamondback network stats, Sidewinder network endpoints
 * - Hourly: Historical time series (ts04-ts16, including Lightning ts06)
 * - Every 10 min: Lightning current stats (s11), Diamondback stats
 * - Daily: Supply data (s04)
 * - On demand: Price and market data (s07, s08, s12, s15)
 *
 * Reduces API calls from 288/day per endpoint to targeted intervals
 */

import { fetchWithValidation } from '../apiClient';

// Sidewinder endpoints (2140data.net)
const SIDEWINDER_STRATEGIES = {
  onBlock: ['s01', 's02', 's03', 's06', 's17'],   // Network stats + forex rates
  hourly: ['ts04', 'ts05', 'ts06', 'ts07', 'ts08', 'ts09', 'ts10', 'ts11', 'ts13', 'ts14', 'ts15', 'ts16', 's16'], // Historical data + Fear/Greed
  every10min: ['s11', 's17'],                      // Lightning current + forex rates
  daily: [],                                       // Supply (s04 disabled - endpoint returns 404)
  onDemand: ['s07', 's08', 's09', 's12', 's15']   // Price/market (added s09 for exchange prices)
};

// Diamondback endpoints (2140data.com)
const DIAMONDBACK_ENDPOINTS = {
  blockchainStats: '/v1/blockchain/stats',             // Derived stats: blocks24h, avgBlockTime, hashrate
  networkStats144: '/v1/stats/network?period=144',     // 24-hour network stats
  networkStats2016: '/v1/stats/network?period=2016',   // Difficulty period stats
  networkStats4032: '/v1/stats/network?period=4032',   // 4-week stats
  difficultyAdjustment: '/v1/difficulty-adjustment',   // Difficulty progress
  mempool: '/mempool',                                 // Mempool stats
  feeRecommended: '/v1/fees/recommended'               // Fee estimates
};

const SIDEWINDER_BASE_URL = 'https://2140data.net/api';
const DIAMONDBACK_BASE_URL = 'https://2140data.com/api';

export class SmartPollingManager {
  constructor(apiKey) {
    this.apiKey = apiKey;
    this.timers = new Map();
    this.lastFetch = new Map();
    this.diamondbackData = {};
  }

  /**
   * Start all interval-based polling
   */
  start() {
    console.log('[SmartPolling] Starting intelligent polling strategies');

    // Hourly refresh for historical data
    this.timers.set('hourly', setInterval(() => {
      console.log('[SmartPolling] Hourly refresh triggered');
      this.fetchSidewinderEndpoints(SIDEWINDER_STRATEGIES.hourly);
      this.fetchDiamondback(['networkStats2016', 'networkStats4032']);
    }, 3600000)); // 1 hour

    // Every 10 minutes for Lightning + Diamondback stats
    this.timers.set('every10min', setInterval(() => {
      console.log('[SmartPolling] 10-minute refresh triggered');
      this.fetchSidewinderEndpoints(SIDEWINDER_STRATEGIES.every10min);
      this.fetchDiamondback(['networkStats144', 'difficultyAdjustment', 'mempool', 'feeRecommended']);
    }, 600000)); // 10 minutes

    // Daily refresh for supply data
    this.timers.set('daily', setInterval(() => {
      console.log('[SmartPolling] Daily refresh triggered');
      this.fetchSidewinderEndpoints(SIDEWINDER_STRATEGIES.daily);
    }, 86400000)); // 24 hours

    console.log('[SmartPolling] All interval timers started');
  }

  /**
   * Triggered by BlockEventManager on new blocks
   * Refreshes network stats that change with each block
   */
  async onBlockEvent() {
    console.log('[SmartPolling] Block event refresh triggered');

    // Fetch both Sidewinder and Diamondback in parallel
    const [sidewinderResult, diamondbackResult] = await Promise.all([
      this.fetchSidewinderEndpoints(SIDEWINDER_STRATEGIES.onBlock),
      this.fetchDiamondback(['networkStats144', 'difficultyAdjustment', 'mempool'])
    ]);

    // Merge results
    return {
      data: { ...sidewinderResult.data },
      metadata: { ...sidewinderResult.metadata },
      diamondback: diamondbackResult,
      summary: sidewinderResult.summary
    };
  }

  /**
   * Fetch initial data for all endpoints
   * Called once on app startup
   */
  async fetchAllEndpoints() {
    console.log('[SmartPolling] Fetching all endpoints (initial load)');

    // Collect all Sidewinder endpoints
    const allSidewinderEndpoints = [
      ...SIDEWINDER_STRATEGIES.onBlock,
      ...SIDEWINDER_STRATEGIES.hourly,
      ...SIDEWINDER_STRATEGIES.every10min,
      ...SIDEWINDER_STRATEGIES.daily,
      ...SIDEWINDER_STRATEGIES.onDemand
    ];

    // Remove duplicates
    const uniqueSidewinderEndpoints = [...new Set(allSidewinderEndpoints)];

    // All Diamondback endpoints
    const allDiamondbackEndpoints = Object.keys(DIAMONDBACK_ENDPOINTS);

    // Fetch both in parallel
    const [sidewinderResult, diamondbackResult] = await Promise.all([
      this.fetchSidewinderEndpoints(uniqueSidewinderEndpoints),
      this.fetchDiamondback(allDiamondbackEndpoints)
    ]);

    return {
      data: { ...sidewinderResult.data },
      metadata: { ...sidewinderResult.metadata },
      diamondback: diamondbackResult,
      summary: sidewinderResult.summary
    };
  }

  /**
   * Fetch specific Sidewinder endpoints
   * @param {Array} endpoints - Array of endpoint names
   * @returns {Promise<Object>} { data, metadata, summary }
   */
  async fetchSidewinderEndpoints(endpoints) {
    const urls = endpoints.map(ep =>
      `${SIDEWINDER_BASE_URL}/${ep}/latest?api_key=${this.apiKey}`
    );

    const startTime = performance.now();
    const result = await fetchWithValidation(urls);
    const duration = performance.now() - startTime;

    console.log(`[SmartPolling] Fetched ${endpoints.length} Sidewinder endpoints in ${duration.toFixed(0)}ms`);

    // Track last fetch time
    endpoints.forEach(ep => {
      this.lastFetch.set(`sidewinder:${ep}`, Date.now());
    });

    return result;
  }

  /**
   * Fetch Diamondback endpoints
   * @param {Array} endpointKeys - Array of DIAMONDBACK_ENDPOINTS keys
   * @returns {Promise<Object>} Diamondback data
   */
  async fetchDiamondback(endpointKeys) {
    console.log(`[SmartPolling] Fetching ${endpointKeys.length} Diamondback endpoints`);

    const startTime = performance.now();
    const results = {};
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);

    await Promise.all(
      endpointKeys.map(async (key) => {
        const url = `${DIAMONDBACK_BASE_URL}${DIAMONDBACK_ENDPOINTS[key]}`;

        try {
          const response = await fetch(url, { signal: controller.signal });
          if (response.ok) {
            results[key] = await response.json();
            this.lastFetch.set(`diamondback:${key}`, Date.now());
          } else {
            console.warn(`[SmartPolling] Diamondback ${key} failed: ${response.status}`);
            results[key] = null;
          }
        } catch (error) {
          if (error.name === 'AbortError') {
            console.warn(`[SmartPolling] Diamondback ${key} timed out`);
          } else {
            console.warn(`[SmartPolling] Diamondback ${key} error:`, error.message);
          }
          results[key] = null;
        }
      })
    );

    clearTimeout(timeoutId);
    const duration = performance.now() - startTime;
    console.log(`[SmartPolling] Fetched Diamondback data in ${duration.toFixed(0)}ms`);

    // Store for access
    this.diamondbackData = { ...this.diamondbackData, ...results };

    return results;
  }

  /**
   * Manually refresh on-demand endpoints
   * Used for price updates or user-triggered refreshes
   */
  async refreshOnDemand() {
    console.log('[SmartPolling] On-demand refresh triggered');
    return this.fetchSidewinderEndpoints(SIDEWINDER_STRATEGIES.onDemand);
  }

  /**
   * Get Diamondback data
   * @returns {Object} All Diamondback data
   */
  getDiamondbackData() {
    return { ...this.diamondbackData };
  }

  /**
   * Get specific Diamondback endpoint data
   * @param {string} key - Endpoint key
   * @returns {*} Endpoint data or null
   */
  getDiamondbackEndpoint(key) {
    return this.diamondbackData[key] || null;
  }

  /**
   * Get time since last fetch for an endpoint
   * @param {string} endpoint - Endpoint name (with optional source prefix)
   * @returns {number|null} Milliseconds since last fetch
   */
  getTimeSinceLastFetch(endpoint) {
    const lastFetch = this.lastFetch.get(endpoint);
    return lastFetch ? Date.now() - lastFetch : null;
  }

  /**
   * Get all refresh strategies
   * @returns {Object} Refresh strategies map
   */
  getStrategies() {
    return {
      sidewinder: { ...SIDEWINDER_STRATEGIES },
      diamondback: { ...DIAMONDBACK_ENDPOINTS }
    };
  }

  /**
   * Stop all polling intervals
   */
  cleanup() {
    console.log('[SmartPolling] Stopping all polling timers');
    this.timers.forEach((timer, name) => {
      clearInterval(timer);
      console.log(`[SmartPolling] Stopped ${name} timer`);
    });
    this.timers.clear();
    this.lastFetch.clear();
  }

  /**
   * Get status report of all endpoints
   * @returns {Object} Status by endpoint
   */
  getStatusReport() {
    const report = {
      sidewinder: {},
      diamondback: {}
    };

    // Sidewinder endpoints
    Object.entries(SIDEWINDER_STRATEGIES).forEach(([strategy, endpoints]) => {
      endpoints.forEach(endpoint => {
        const timeSince = this.getTimeSinceLastFetch(`sidewinder:${endpoint}`);
        report.sidewinder[endpoint] = {
          strategy,
          lastFetch: timeSince,
          lastFetchFormatted: timeSince ? `${Math.floor(timeSince / 1000)}s ago` : 'Never'
        };
      });
    });

    // Diamondback endpoints
    Object.keys(DIAMONDBACK_ENDPOINTS).forEach(key => {
      const timeSince = this.getTimeSinceLastFetch(`diamondback:${key}`);
      report.diamondback[key] = {
        lastFetch: timeSince,
        lastFetchFormatted: timeSince ? `${Math.floor(timeSince / 1000)}s ago` : 'Never',
        hasData: this.diamondbackData[key] !== null && this.diamondbackData[key] !== undefined
      };
    });

    return report;
  }
}
